import { useCallback, useEffect, useMemo } from 'react'
import Header from '../../components/ui/Layout/Header'
import ContainerContent from '../../components/ui/Layout/ContainerContent'
import {
  Alert,
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  Progress,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import { AddIcon } from '@chakra-ui/icons'
import { useNavigate } from 'react-router-dom'
import { ComboBox, ComboBoxOption } from '../../components/ui/ComboBox/ComboBox'

import rootStore from '../../store/RootStore'
import { observer } from 'mobx-react'
import { useFetchUserPlansQuery } from './hooks/useFetchPlansQuery'
import ReportListContainer from './components/ReportListContainer'
import CreateSnapshotDialog from './components/CreateSnapshotDialog'
import CustomMenuList from '../../components/ui/CustomMenuList/CustomMenuList'
import { useFetchReportsByPlanIdQuery } from './hooks/useFetchReportsByPlanIdQuery'
import { ApiErrorKind, apiErrorKindOf } from '../../services/apiErrors'
import { AccessRole } from '../../types/AuthTypes'

import ReactGA from 'react-ga4'

// ComboBox identifies options by string. Plan ids are ints, so every site that puts one in
// or matches one back goes through here — three places that must agree.
const planOptionValue = (planId?: number) => String(planId ?? '')

const HomePage = observer(() => {
  const {
    wizardStore: { invalidateReportWizard },
    planStore: { currentPlan, setCurrentPlan },
    // DashboardV2 serves PABS only, so there is no product control - ProductStore's constructor
    // pins 'pa' and nothing changes it.
    productsStore: { currentProduct },
    reportStore: {
      reportListIds,
    },
    authStore: { userAccess },
  } = rootStore

  const isAdmin = userAccess === AccessRole.CemAdmin


  const navigate = useNavigate()
  const snapshotDialog = useDisclosure()

  // One list for everyone, employees included: the backend returns exactly the plans this caller
  // may open, so there is nothing left for the client to decide from a role claim.
  const plans = useFetchUserPlansQuery({ productId: currentProduct })
  const planList = useMemo(() => plans.data ?? [], [plans.data])
  const hasPlans = planList.length > 0
  const showNoPlansMessage = !hasPlans && !plans.isLoading && !plans.isError

  // With one plan there is nothing to choose, so choosing it is not a decision the user needs to
  // make. Re-fires after a product switch, which clears the current plan, and corrects a persisted
  // plan that is no longer the one this caller is entitled to.
  useEffect(() => {
    if (planList.length !== 1) return
    const onlyPlan = planList[0]
    if (currentPlan?.planId !== onlyPlan.planId) setCurrentPlan(onlyPlan)
  }, [planList, currentPlan, setCurrentPlan])

  const products = useFetchReportsByPlanIdQuery({
    planId: currentPlan?.planId,
    productId: currentProduct,
  })

  const reportCount = useMemo(
    () =>
      (products.data ?? []).reduce(
        (total, product) => total + product.reports.length,
        0,
      ),
    [products.data],
  )

  // `initialData: []` pins `isLoading` false for the query's whole life, so in-flight is `isFetching`.
  const showNoReportsMessage =
    hasPlans &&
    !!currentPlan?.planId &&
    !products.isFetching &&
    !products.isError &&
    reportCount === 0

  const handlePlanChange = useCallback(
    ({ value }: ComboBoxOption) => {
      const plan = planList.find(plan => planOptionValue(plan.planId) === value)

      if (plan) {
        setCurrentPlan(plan)
      }
    },
    [setCurrentPlan, planList],
  )

  const planDropdownOptions = useMemo(
    () =>
      planList.map(plan => ({
        label: plan.planName,
        value: planOptionValue(plan.planId),
      })),
    [planList],
  )

  const handleOpenWizard = useCallback(() => {
    /**
     * Reset the wizard;
     */
    invalidateReportWizard()
    navigate('/create-report/1')
  }, [invalidateReportWizard, navigate])

  const createMenuItems = useMemo(
    () => [
      {
        id: 'snapshot',
        label: 'Snapshot report (as of a date)',
        action: () => snapshotDialog.onOpen(),
      },
    ],
    [snapshotDialog],
  )

  const handleReportCardClick = useCallback((report) => {
    ReactGA.event({
      category: 'journey',
      action: 'navigate-to-report',
      label: reportListIds?.join(', '),
    })
    navigate(
      report.isSnapshot
        ? `/reports/LiveReport?snapshot=${report.snapshotId}`
        : `/reports/${report.id}`,
    )
  }, [navigate, reportListIds])

  return (
    <>
      <Header
        title="Your Reports"
        reportTitle="CEM Dashboard"
        position="fixed"
        zIndex={2}
      />
      {currentProduct === 'pa' ? (
        isAdmin && (
          <Menu>
            <Tooltip hasArrow label={!currentPlan?.planId ? 'Select a plan first!' : 'Create a report'} bg='brand.gray' color='black'>
              <MenuButton
                as={IconButton}
                aria-label="Create Report"
                icon={<AddIcon />}
                right={'40px'}
                bottom={'40px'}
                width={'60px'}
                height={'60px'}
                fontSize={'20px'}
                isRound={true}
                position={'fixed'}
                float={'right'}
                color="brand.white"
                backgroundColor="brand.orange"
                isDisabled={!currentPlan?.planId}
                title={''}
                _hover={{
                  backgroundColor: 'brand.lightOrange',
                  transform: 'scale(1.1)',
                }}
              />
            </Tooltip>
            <CustomMenuList items={createMenuItems} />
          </Menu>
        )
      ) : (
        <Tooltip hasArrow label={!currentPlan?.planId ? 'Select a plan first!' : 'Create a report'} bg='brand.gray' color='black'>
          <IconButton
            aria-label="Create Report"
            icon={<AddIcon />}
            right={'40px'}
            bottom={'40px'}
            width={'60px'}
            height={'60px'}
            fontSize={'20px'}
            isRound={true}
            position={'fixed'}
            float={'right'}
            color="brand.white"
            backgroundColor="brand.orange"
            isDisabled={!currentPlan?.planId}
            title={''}
            _hover={{
              backgroundColor: 'brand.lightOrange',
              transform: 'scale(1.1)',
            }}
            onClick={handleOpenWizard}
          />
        </Tooltip>
      )}
      <ContainerContent pt="180px">
        {hasPlans ? (
          <Flex
            marginTop="32px"
            position="relative"
            textStyle="h2">
            <ComboBox
              options={planDropdownOptions}
              onSelect={handlePlanChange}
              placeholder="Type the plan name"
              preSelected={planDropdownOptions.find(
                option => option.value === planOptionValue(currentPlan?.planId),
              )}
              width="100%"
            />
          </Flex>
        ) : null}
        {plans.isLoading ? (
          <Progress
            isIndeterminate
            height="2px"
          />
        ) : null}
        {plans.isError ? (
          <Alert
            status="error"
            mt="20px">
            <Text textStyle="p">
              We couldn&apos;t load your plans.{' '}
              <Button
                variant="link"
                textStyle="link"
                onClick={() => window.location.reload()}>
                Reload page
              </Button>
            </Text>
          </Alert>
        ) : null}
        {showNoPlansMessage ? (
          <Box
            p={18}
            mt={18}
            bgColor="brand.lightestGray">
            <Center>
              You don&apos;t have access to any plans in this product yet. If
              you were expecting one, contact your CEM representative.
            </Center>
          </Box>
        ) : null}
        {hasPlans && !currentPlan?.planId && (
          <Box
            p={18}
            mt={18}
            bgColor="brand.lightestGray">
            <Center>Select a plan to show the Reports</Center>
          </Box>
        )}

        {products.isError ? (
          <Box
            p={18}
            mt={18}
            bgColor="brand.lightestGray">
            <Center>
              {apiErrorKindOf(products.error) === ApiErrorKind.Forbidden
                ? "You don't have access to this plan's reports. If you were expecting access, contact your CEM representative."
                : "We couldn't load the reports for this plan."}
            </Center>
          </Box>
        ) : null}

        {showNoReportsMessage ? (
          <Box
            p={18}
            mt={18}
            bgColor="brand.lightestGray">
            <Center>
              There are no reports for this plan yet. If you were expecting one,
              contact your CEM representative.
            </Center>
          </Box>
        ) : null}

        {!products.isLoading && !products.isError && (
          <ReportListContainer
            products={products.data}
            onItemClick={handleReportCardClick}
            isAdmin={isAdmin}
          />
        )}
      </ContainerContent>

      <CreateSnapshotDialog
        isOpen={snapshotDialog.isOpen}
        onClose={snapshotDialog.onClose}
      />
    </>
  )
})

export default HomePage
